/**
 * 008-reporting-dashboards T008/T010/T011 — moved out of `dashboard.tsx` unchanged. `010` has
 * already landed (`tallySync.ts`, wired and tested), so this renders real data, not the union's
 * `unbuilt` arm tasks.md anticipated. Escalation (T011, FR-006, §19) is the API's own `alertRaised`
 * boolean, computed server-side against the configured `heartbeatAlertPeriod` — never a client-side
 * "now minus lastHeartbeatAt > threshold" comparison, which would be a second copy of a threshold the
 * API already owns. Verified live against the running gateway 2026-09-09: a silent agent answered
 * `{state:"silent", alertRaised:true}` and rendered "Needs attention" here. Drawn by `StatusCard`.
 */
import { PiClockDuotone as Clock, PiCloudWarningDuotone as ServerCrash } from "react-icons/pi";
import { TileBoundary } from "./TileBoundary";
import { StatusCard, UnreadableStatusCard } from "./StatusCard";
import { formatMoment, type AgentStatus } from "../../lib/services/tallySync";

function unreadable(error: unknown) {
  return (
    <UnreadableStatusCard
      title="Tally agent heartbeat"
      icon={<ServerCrash aria-hidden className="size-5" />}
      error={error}
      fallback="Could not be read — this is not the same as no alert."
    />
  );
}

export function AgentHeartbeatTile({ resolve }: { resolve: Promise<AgentStatus> }) {
  return (
    <TileBoundary resolve={resolve} errorFallback={unreadable}>
      {(status) => (
        <StatusCard
          title="Tally agent heartbeat"
          icon={status.alertRaised ? <ServerCrash aria-hidden className="size-5" /> : <Clock aria-hidden className="size-5" />}
          tone={status.alertRaised ? "destructive" : status.state === "not_configured" || status.stubbed ? "warning" : "success"}
          value={status.alertRaised ? "Needs attention" : status.state === "not_configured" ? "Not configured" : status.stubbed ? "Stubbed agent" : "Reporting"}
          word
          caption={status.lastHeartbeatAt ? `Last heard ${formatMoment(status.lastHeartbeatAt)}` : "Never — no agent has ever checked in."}
          link={{ to: "/integrations/tally", label: "Open the Tally agent" }}
        />
      )}
    </TileBoundary>
  );
}
